import React, { useState, useEffect, useRef } from "react";
import { useTheme } from "../../../contexts/theme-context";

const PINK      = "#E40186";
const PINK_BG   = "#fdf0f8";
const PINK_BADGE = "#fce8f4";
const DIVIDER   = "#f0eef8";

// ── Inline SVG icons (no webfont dependency) ──────────────────────
const IconFolderPlus = ({ color }) => (
  <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">
    <path d="M5 4h4l3 3h7a2 2 0 0 1 2 2v8a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V6a2 2 0 0 1 2-2"/>
    <line x1="12" y1="10" x2="12" y2="16"/>
    <line x1="9"  y1="13" x2="15" y2="13"/>
  </svg>
);

const IconSearch = ({ color }) => (
  <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">
    <circle cx="11" cy="11" r="7"/>
    <line x1="16.5" y1="16.5" x2="22" y2="22"/>
  </svg>
);

const IconList = ({ color }) => (
  <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">
    <line x1="9"  y1="6"  x2="20" y2="6"/>
    <line x1="9"  y1="12" x2="20" y2="12"/>
    <line x1="9"  y1="18" x2="20" y2="18"/>
    <line x1="5"  y1="6"  x2="5"  y2="6.01"/>
    <line x1="5"  y1="12" x2="5"  y2="12.01"/>
    <line x1="5"  y1="18" x2="5"  y2="18.01"/>
  </svg>
);

const IconDownload = ({ color }) => (
  <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">
    <path d="M4 17v2a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2v-2"/>
    <polyline points="7 11 12 16 17 11"/>
    <line x1="12" y1="4" x2="12" y2="16"/>
  </svg>
);

const IconTrash = ({ color }) => (
  <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">
    <polyline points="3 6 5 6 21 6"/>
    <path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6"/>
    <path d="M10 11v6"/>
    <path d="M14 11v6"/>
    <path d="M9 6V4a1 1 0 0 1 1-1h4a1 1 0 0 1 1 1v2"/>
  </svg>
);

const IconFileCertificate = ({ color }) => (
  <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">
    <path d="M14 3v4a1 1 0 0 0 1 1h4"/>
    <path d="M17 21H7a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h7l5 5v11a2 2 0 0 1-2 2z"/>
    <circle cx="12" cy="15" r="2"/>
    <path d="M10.4 17.4L9 19l1 1 2-1 2 1 1-1-1.4-1.6"/>
  </svg>
);

const IconCheck = ({ color }) => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
    <polyline points="20 6 9 17 4 12"/>
  </svg>
);

// ── Step definitions ──────────────────────────────────────────────
const STEPS = [
  {
    name: "Create a project",
    sub:  "Organise tracks by film, episode, or brand brief.",
    Icon: IconFolderPlus,
  },
  {
    name: "Search 1M+ tracks",
    sub:  "Reference track, similarity, and prompt search.",
    Icon: IconSearch,
  },
  {
    name: "Add tracks to project",
    sub:  "Shortlist, compare, and curate your selection.",
    Icon: IconList,
  },
  {
    name: "Download and create",
    sub:  "High-quality WAV files, ready for your edit.",
    Icon: IconDownload,
  },
  {
    name: "Remove unused tracks",
    sub:  "Only license what you actually use.",
    Icon: IconTrash,
  },
  {
    name: "Generate your licence",
    sub:  "One click. Instant worldwide PDF in your name.",
    Icon: IconFileCertificate,
  },
];

// ── Component ─────────────────────────────────────────────────────
export default function LicenseFlowCard({autoPlay = true }) {
  const { theme } = useTheme();
  const [active, setActive] = useState(0);
  const [darkMode, setDarkMode] = useState(theme === "dark");
  const timer = useRef(null);
  
  
  const startTimer = () => {
    if (timer.current) clearInterval(timer.current);
    if (!autoPlay) return;
    timer.current = setInterval(() => {
      setActive((s) => (s + 1) % STEPS.length);
    }, 2400);
  };

  useEffect(() => {
    startTimer();
    return () => clearInterval(timer.current);
  }, []);

  useEffect(() => {
    setDarkMode(theme === "dark" ? true : false)
  },[theme])
  
  const handleClick = (i) => {
    setActive(i);
    startTimer();
  };

  const pct = Math.round(((active + 1) / STEPS.length) * 100);

  // ── Theme tokens ────────────────────────────────────────────────
  const t = darkMode ? {
    cardBg:     "#1a1530",
    divider:    "#2e2850",
    progBg:     "#2e2850",
    rowActive:  "#2a1f3d",
    titleIdle:  "#e8e4ff",
    titleDone:  "#c8c4e8",
    sub:        "#7a749a",
    iconIdle:   "#4a4668",
    badgeInact: "#2e2850",
    badgeNum:   PINK,
  } : {
    cardBg:     "#ffffff",
    divider:    DIVIDER,
    progBg:     DIVIDER,
    rowActive:  PINK_BG,
    titleIdle:  "#1a1240",
    titleDone:  "#1a1240",
    sub:        "#9b97b2",
    iconIdle:   "#c8c4dc",
    badgeInact: PINK_BADGE,
    badgeNum:   PINK,
  };

  return (
    <div className="card" style={{
      borderRadius: 18,
      overflow:     "hidden",
      fontFamily:   "'Plus Jakarta Sans', sans-serif",
      width:        "100%",
      maxWidth:     440,
    }}>

      {/* Progress bar */}
      <div style={{ height: 4, background: t.progBg }}>
        <div style={{
          height:     "100%",
          background: PINK,
          width:      `${pct}%`,
          transition: "width 0.4s ease",
        }} />
      </div>

      {/* Steps */}
      {STEPS.map((step, i) => {
        const isDone   = i < active;
        const isActive = i === active;
        const isLast   = i === STEPS.length - 1;
        const iconColor = isActive ? PINK : t.iconIdle;

        return (
          <div
            key={i}
            onClick={() => handleClick(i)}
            style={{
              display:      "flex",
              alignItems:   "center",
              gap:          16,
              padding:      "20px 24px",
              borderBottom: isLast ? "none" : `1px solid ${t.divider}`,
              background:   isActive ? t.rowActive : "transparent",
              cursor:       "pointer",
              transition:   "background 0.2s",
            }}
          >
            {/* Badge */}
            <div style={{
              width:          42,
              height:         42,
              minWidth:       42,
              borderRadius:   "50%",
              background:     (isDone || isActive) ? PINK : t.badgeInact,
              display:        "flex",
              alignItems:     "center",
              justifyContent: "center",
              transition:     "background 0.2s",
            }}>
              {isDone
                ? <IconCheck color="#fff" />
                : (
                  <span style={{
                    color:      isActive ? "#fff" : t.badgeNum,
                    fontSize:   isActive ? 16 : 15,
                    fontWeight: 700,
                    lineHeight: 1,
                  }}>
                    {i + 1}
                  </span>
                )
              }
            </div>

            {/* Text */}
            <div style={{ flex: 1 }}>
              <div style={{
                fontSize:   15.5,
                fontWeight: 700,
                color:      isActive ? PINK : isDone ? t.titleDone : t.titleIdle,
                marginBottom: 3,
                transition: "color 0.2s",
              }}>
                {step.name}
              </div>
              <div style={{
                fontSize:   13,
                color:      t.sub,
                lineHeight: 1.45,
              }}>
                {step.sub}
              </div>
            </div>

            {/* Action icon */}
            <step.Icon color={iconColor} />
          </div>
        );
      })}
    </div>
  );
}
